import React, { useState } from 'react';
import { Upload, CreditCard, FileSearch, Mail, ChevronRight, ChevronLeft, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

const STEPS = [
  {
    icon: Upload,
    title: "Sube tu contrato",
    description: "Sube contratos en PDF, Word o imagen. Nuestro sistema de OCR avanzado extrae el texto completo automáticamente para su análisis.",
    color: "#0B5D6E"
  },
  {
    icon: CreditCard,
    title: "Realiza tu pago",
    description: "Paga de forma segura con Stripe. Cada análisis tiene un costo único por contrato, sin suscripciones ni cargos ocultos.",
    color: "#D97706"
  },
  {
    icon: FileSearch,
    title: "Recibe tu análisis",
    description: "Nuestra IA analiza cada cláusula: identifica riesgos, detecta cláusulas abusivas, calcula un score de riesgo y genera un reporte PDF profesional descargable.",
    color: "#059669"
  },
  {
    icon: Mail,
    title: "Alertas por correo",
    description: "Configura la fecha de vencimiento de tu contrato y recibe alertas automáticas por email a los 30, 15 y 7 días antes. Nunca olvides renovar o cancelar a tiempo.",
    color: "#DC2626"
  }
];

const OnboardingWizard = ({ onComplete, userId }) => {
  const [step, setStep] = useState(0);
  const current = STEPS[step];
  const Icon = current.icon;

  const handleComplete = () => {
    localStorage.setItem(`cc360_onboarding_done_${userId}`, 'true');
    onComplete();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="onboarding-wizard">
      <div className="bg-white rounded-xl max-w-lg w-full shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b" style={{ backgroundColor: '#0B5D6E' }}>
          <h3 className="text-white font-semibold text-sm">Bienvenido a ContractCheck360</h3>
          <button
            onClick={handleComplete}
            className="text-white/70 hover:text-white transition-colors"
            data-testid="onboarding-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Step indicators */}
        <div className="flex gap-2 px-5 pt-5 justify-center">
          {STEPS.map((_, i) => (
            <div
              key={i}
              className="h-1.5 rounded-full transition-all duration-300"
              style={{
                width: i === step ? '2rem' : '0.75rem',
                backgroundColor: i === step ? current.color : '#E5E7EB'
              }}
            />
          ))}
        </div>

        {/* Content */}
        <div className="px-8 pt-6 pb-8 text-center">
          <div
            className="w-16 h-16 rounded-2xl mx-auto mb-5 flex items-center justify-center"
            style={{ backgroundColor: `${current.color}12` }}
          >
            <Icon className="w-8 h-8" style={{ color: current.color }} />
          </div>
          <h2 className="text-xl font-bold mb-3" style={{ color: '#1B2A4A' }} data-testid="onboarding-step-title">
            {current.title}
          </h2>
          <p className="text-gray-500 text-sm leading-relaxed max-w-sm mx-auto">
            {current.description}
          </p>
          <p className="text-xs text-gray-400 mt-5">
            Paso {step + 1} de {STEPS.length}
          </p>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3.5 border-t bg-gray-50">
          <button
            onClick={handleComplete}
            className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
            data-testid="onboarding-skip-btn"
          >
            Omitir
          </button>
          <div className="flex gap-2">
            {step > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setStep(s => s - 1)}
                data-testid="onboarding-prev-btn"
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> Anterior
              </Button>
            )}
            {step < STEPS.length - 1 ? (
              <Button
                size="sm"
                onClick={() => setStep(s => s + 1)}
                style={{ backgroundColor: current.color }}
                data-testid="onboarding-next-btn"
              >
                Siguiente <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={handleComplete}
                style={{ backgroundColor: '#0B5D6E' }}
                data-testid="onboarding-start-btn"
              >
                ¡Comenzar!
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizard;
